/** * ステイタスタイプ選択コンポーネント */
<template>
  <div class="status-selector">
    <div class="selected-status-type">
      <b-form-input
        class="selected-status-type-input"
        size="sm"
        v-bind:readonly="true"
        v-bind:value="selectedStatusTypeNames"
        v-on:click="$_showDialog"
      ></b-form-input>
    </div>
    <ifield-b-modal
      v-bind:id="modalId"
      v-bind:title="$t('appStatusTypeSelector.message.selectStatusTypes')"
      v-bind:ok-title="$t('appStatusTypeSelector.action.select')"
      v-bind:cancel-title="$t('appStatusTypeSelector.action.cancel')"
      ok-variant="success"
      size="md"
      centered
      v-on:ok="$_confirmStatusTypes"
      v-on:show="$_initSelectedStatusTypes"
    >
      <loading-mask
        v-bind:isFullScreen="isFullScreenLoading"
        v-bind:isLoading="isLoading"
      >
        <!-- ステイタス一覧 -->
        <div class="selector-data-list">
          <table class="table table-hover selector-table">
            <thead class="thead-light">
              <tr>
                <th scope="col" class="field-checkbox">
                  <b-form-checkbox
                    v-if="isSelectTypeMultiple"
                    v-model="isSelectedAllStatusTypes"
                    v-bind:indeterminate="indeterminateStatusTypes"
                    v-on:change="$_toggleAll"
                  >
                  </b-form-checkbox>
                </th>
                <th scope="col">
                  {{ $t('appStatusTypeSelector.statusTypeName') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="statusType in statusTypes"
                v-bind:key="statusType.id"
                v-on:click.prevent="$_onClickStatusRow(statusType.id)"
              >
                <td>
                  <!-- 複数選択用チェックボックス -->
                  <!-- inputイベントのemit回避のためネイティブのinputを使用 -->
                  <div
                    v-if="isSelectTypeMultiple"
                    class="custom-control custom-checkbox"
                  >
                    <input
                      type="checkbox"
                      class="custom-control-input"
                      v-bind:id="`${modalId}-check-${statusType.id}`"
                      v-bind:value="statusType.id"
                      v-bind:checked="multiStatusTypeIdSet.has(statusType.id)"
                    />
                    <label
                      class="custom-control-label"
                      v-bind:for="`${modalId}-check-${statusType.id}`"
                    />
                  </div>
                  <!-- 単一選択用ラジオボタン -->
                  <!-- inputイベントのemit回避のためネイティブのinputを使用 -->
                  <div
                    v-if="!isSelectTypeMultiple"
                    class="custom-control custom-radio"
                  >
                    <input
                      type="radio"
                      class="custom-control-input"
                      v-bind:id="`${modalId}-radio-${statusType.id}`"
                      v-bind:value="statusType.id"
                      v-bind:checked="statusType.id === singleStatusTypeId"
                    />
                    <label
                      class="custom-control-label"
                      v-bind:for="`${modalId}-radio-${statusType.id}`"
                    />
                  </div>
                </td>
                <td>
                  <div class="item-name-col">
                    <div>
                      <h6>{{ statusType.status_type_name }}</h6>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </loading-mask>
    </ifield-b-modal>
  </div>
</template>

<script>
import { RestApiClient } from 'ifield-core-components'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'

/**
 * 選択種別
 */
const SELECT_TYPES = {
  /** 単一 */
  SINGLE: 0,
  /** 複数 */
  MULTIPLE: 1,
}

export default {
  name: 'AppStatusTypeSelector',
  props: {
    /**
     * 選択されているステイタスIDのデフォルト値
     */
    defaultStatusTypes: {
      type: Array,
      default: function () {
        return []
      },
    },

    /**
     * 選択種別
     * (0:単一、1:複数)
     * */
    selectType: {
      type: Number,
      default: SELECT_TYPES.MULTIPLE,
    },

    /**
     * すべてのステイタスを選択している時に表示されるラベル(必須プロパティ)
     */
    selectedAllStatusLabel: {
      type: String,
      required: true,
    },

    /**
     * DOMのidのプレフィックス
     * 1画面に本コンポーネントを複数配置する場合は設定が必要です
     */
    idPrefix: {
      type: String,
      default: '',
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      /**
       * ステイタスタイプ一覧
       * @type {Array}
       */
      statusTypes: null,
      /**
       * ダイアログで選択されているステイタスタイプ(複数用)
       */
      multiStatusTypeIds: [],
      /**
       * ダイアログで選択されているステイタスタイプ(単一用)
       */
      singleStatusTypeId: null,
      /**
       * 選択されているステイタスタイプ名(確定済み)
       */
      selectedStatusTypeNames: null,
      /**
       * すべてを選択している場合はtrue
       */
      isSelectedAllStatusTypes: false,
      /**
       * 表示されているステイタス一覧の一部のステイタスを選択している場合はtrue
       */
      indeterminateStatusTypes: false,
      /**
       * 選択が確定しているステイタスタイプID
       */
      confirmedStatusTypeIds: this.defaultStatusTypes.slice(
        0,
        this.defaultStatusTypes.length
      ),
      /**
       * ロード中の場合はtrue
       */
      isLoading: false,
      /**
       * ローディングをフルスクリーン表示する場合はtrue
       */
      isFullScreenLoading: false,
    }
  },

  computed: {
    /**
     * 選択種別
     */
    SELECT_TYPES: function () {
      return SELECT_TYPES
    },

    isSelectTypeMultiple: function () {
      return this.selectType === this.SELECT_TYPES.MULTIPLE
    },

    /**
     * 選択されているステイタス種類のSet(複数用)
     * @type {Set}
     */
    multiStatusTypeIdSet: function () {
      return new Set(this.multiStatusTypeIds)
    },

    /**
     * 現在表示されているステイタスタイプのステイタスタイプIDをキーにしたMapを返却する
     * @return {Map<number, object>} キーはステイタスタイプID、値は{id, name}
     */
    statusTypeMap: function () {
      let statusTypeMap = new Map()
      if (this.statusTypes) {
        this.statusTypes.forEach((statusType) => {
          statusTypeMap.set(statusType.id, statusType)
        })
      }
      return statusTypeMap
    },

    /**
     * モーダルダイアログのID
     * @return {string} モーダルのID
     */
    modalId: function () {
      let modalId = 'status-dialog'
      if (this.idPrefix) {
        modalId = this.idPrefix + modalId
      }
      return modalId
    },
  },
  watch: {
    statusTypes: function () {
      this.setSelectedAllStatus()
    },

    multiStatusTypeIds: function () {
      this.setSelectedAllStatus()
    },
  },
  created: function () {
    this.$_initSelectedStatusTypes()
    this.$_showStatusTypes()
    this.$_showSelectedStatusTypeName()
  },
  methods: {
    /**
     * ステイタスタイプ一覧を表示する
     */
    $_showStatusTypes: async function () {
      this.$_showLoading()

      var url = RestApiClient.createApiUrl('status_type')
      try {
        const json = await RestApiClient.executeGetApi(url)
        this.statusTypes = json.data
        this.isSelectedAllStatusTypes = false
      } catch (error) {
        this.handleApiError(error)
      } finally {
        this.$_hideLoading()
      }
    },

    /**
     * ステイタス選択ダイアログを表示する
     */
    $_showDialog: function () {
      this.$bvModal.show(this.modalId)
    },

    /**
     * ステイタスタイプ選択終了時のイベントを発生させる
     */
    $_confirmStatusTypes: async function () {
      this.$_showLoading(true)

      let statusTypeIds
      if (this.selectType === this.SELECT_TYPES.MULTIPLE) {
        // 複数選択の場合
        statusTypeIds = this.multiStatusTypeIds.slice(
          0,
          this.multiStatusTypeIds.length
        )
        // ステイタスIDでソートする(statusTypeIdsは選択した順に入っているため)
        statusTypeIds.sort()
      } else {
        // 単一選択の場合
        statusTypeIds = this.singleStatusTypeId ? [this.singleStatusTypeId] : []
      }

      // 確定済みのユーザーIDを保持しておく
      this.confirmedStatusTypeIds = statusTypeIds

      let retStatusTypes = []
      let statusTypeNames = []
      // 選択されているステイタスタイプの名前を取得する
      try {
        const json = await RestApiClient.executeGetApi(
          RestApiClient.createApiUrl('status_type')
        )
        let statusTypes = json.data
        let statusTypeMap = new Map()
        statusTypes.forEach((statusType) => {
          statusTypeMap.set(statusType.id, statusType)
        })

        for (let statusTypeId of statusTypeIds) {
          if (statusTypeMap.has(statusTypeId)) {
            let statusType = statusTypeMap.get(statusTypeId)
            retStatusTypes.push({
              id: statusType.id,
              name: statusType.status_type_name,
            })
            statusTypeNames.push(statusType.status_type_name)
          }
        }

        // 選択したステイタスタイプ名をテキストボックスに表示する
        this.selectedStatusTypeNames = this.$_createSelectedStatusTypeNames(
          statusTypeNames,
          statusTypes.length
        )

        this.$emit('selectend-status-types', retStatusTypes)
      } catch (error) {
        this.handleApiError(error)
      } finally {
        this.$_hideLoading()
      }
    },

    /**
     * ステイタスタイプの選択状態を初期化する
     */
    $_initSelectedStatusTypes: function () {
      // 選択状態を初期化する
      if (this.selectType === this.SELECT_TYPES.MULTIPLE) {
        // 複数選択の場合
        this.multiStatusTypeIds = this.confirmedStatusTypeIds.slice(
          0,
          this.confirmedStatusTypeIds.length
        )
      } else {
        // 単一選択の場合
        this.singleStatusTypeId =
          this.confirmedStatusTypeIds.length > 0
            ? this.confirmedStatusTypeIds[0]
            : null
      }
    },

    /**
     * 選択されているステイタスタイプのステイタスタイプ名を表示する
     */
    $_showSelectedStatusTypeName: function () {
      let statusTypeIds
      if (this.selectType === this.SELECT_TYPES.MULTIPLE) {
        // 複数選択の場合
        statusTypeIds = this.multiStatusTypeIds.slice(
          0,
          this.multiStatusTypeIds.length
        )
        // IDでソートする(statusTypeIdsは選択した順に入っているため)
        statusTypeIds.sort()
      } else {
        // 単一選択の場合
        statusTypeIds = this.singleStatusTypeId ? [this.singleStatusTypeId] : []
      }

      let statusTypeNames = []
      // 選択されているステイタスタイプの名前を取得する
      RestApiClient.executeGetApi(RestApiClient.createApiUrl('status_type'))
        .then((json) => {
          let statusTypes = json.data
          let statusTypeMap = new Map()
          statusTypes.forEach((statusType) => {
            statusTypeMap.set(statusType.id, statusType)
          })

          for (let statusTypeId of statusTypeIds) {
            if (statusTypeMap.has(statusTypeId)) {
              statusTypeNames.push(
                statusTypeMap.get(statusTypeId).status_type_name
              )
            }
          }

          // 選択したステイタス名をテキストボックスに表示する
          this.selectedStatusTypeNames = this.$_createSelectedStatusTypeNames(
            statusTypeNames,
            statusTypes.length
          )
        })
        .catch((error) => {
          this.handleApiError(error)
        })
    },

    /**
     * テキストボックスに表示するための選択されていステイタス名を作成する
     * @param {Array<string>} statusTypeNames 選択されているステイタスタイプ名のリスト
     * @param {number} statusTypesCount 全ステイタス数
     * @return {string} 選択されているステイタス名
     */
    $_createSelectedStatusTypeNames: function (
      statusTypeNames,
      statusTypesCount
    ) {
      return statusTypesCount === 0 ||
        statusTypeNames.length === statusTypesCount ||
        statusTypeNames.length === 0
        ? this.selectedAllStatusLabel
        : statusTypeNames.join(',')
    },

    /**
     * ステイタスの選択状態を一括で変更する
     * @param {boolean} checked 一括変更を行う場合はtrue
     */
    $_toggleAll: function (checked) {
      if (checked) {
        // 未選択のステイタスを追加する
        this.statusTypeMap.forEach((statusType) => {
          if (!this.multiStatusTypeIds.includes(statusType.id)) {
            this.multiStatusTypeIds.push(statusType.id)
          }
        })
      } else {
        // 現在のステイタスタイプ一覧に表示されているステイタスタイプを選択済みのリストから除外する
        // (現在の一覧に表示されていないステイタスタイプは選択済みのままとなる)
        let newStatusTypeIds = []
        this.multiStatusTypeIds.forEach((statusTypeId) => {
          if (!this.statusTypeMap.has(statusTypeId)) {
            newStatusTypeIds.push(statusTypeId)
          }
        })
        this.multiStatusTypeIds = newStatusTypeIds
      }
    },

    /**
     * すべてを選択のチェックボックスの状態を設定する
     */
    setSelectedAllStatus: function () {
      let count = 0
      this.multiStatusTypeIds.forEach((statusTypeId) => {
        if (this.statusTypeMap.has(statusTypeId)) {
          count++
        }
      })

      if (count === 0) {
        // 表示されているステイタスタイプ一覧のステイタスが未選択
        this.indeterminateStatusTypes = false
        this.isSelectedAllStatusTypes = false
      } else if (count === this.statusTypeMap.size) {
        // 表示されているステイタスタイプ一覧のステイタスがすべて選択されている
        this.indeterminateStatusTypes = false
        this.isSelectedAllStatusTypes = true
      } else {
        // 一部選択されている
        this.indeterminateStatusTypes = true
        this.isSelectedAllStatusTypes = false
      }
    },

    /**
     * 行クリック時の処理
     * @param {Number} id 対象ステイタスID
     */
    $_onClickStatusRow: function (id) {
      if (this.isSelectTypeMultiple) {
        this.$_onClickMultiSelectedStatusTypes(id)
      } else {
        this.$_onClickSingleSelectedStatusType(id)
      }
    },

    /**
     * 単一用のラジオボタンがクリックされた時の処理
     *
     * @param {Number} value クリックされた値
     */
    $_onClickSingleSelectedStatusType: function (value) {
      if (this.singleStatusTypeId === value) {
        // 同じステイタス種類を選択したら選択解除
        this.singleStatusTypeId = null
      } else {
        this.singleStatusTypeId = value
      }
    },

    /**
     * 複数用のチェックボックスがクリックされた時の処理
     *
     * @param {Number} value クリックされた値
     */
    $_onClickMultiSelectedStatusTypes: function (value) {
      const index = this.multiStatusTypeIds.findIndex((id) => id === value)
      if (index >= 0) {
        // 選択済みのIDを解除
        this.multiStatusTypeIds.splice(index, 1)
      } else {
        this.multiStatusTypeIds.push(value)
      }
    },

    /**
     * ローディングを表示する
     * @param {Boolean=} [isFullScreen=false] フルスクリーンか？
     */
    $_showLoading: function (isFullScreen = false) {
      this.isFullScreenLoading = isFullScreen
      this.isLoading = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoading = false
      this.isFullScreenLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.selected-status-type-input {
  background-color: #ffffff !important;
  cursor: pointer;
}

.selector-data-list {
  width: 100%;
  height: 400px;
  background-color: #ffffff;
  overflow: auto;
  overflow-x: hidden;
  @include mixin-scrollbar;
}

.item-name-col {
  display: flex;
  justify-content: flex-start;
  align-items: bottom;

  h6 {
    margin: 0;
    font-size: 1em;
    line-height: 27px;
    font-weight: bold;
  }
}

table.selector-table {
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 0;

  thead tr th {
    position: sticky;
    top: 0;
    border-top: none;
    z-index: 2;
    user-select: none;
    &.field-checkbox {
      width: 40px;
    }
  }
  tbody tr td {
    user-select: none;
  }
}
</style>
